/**
 * L'ONGLET FACTURES — lot 106, 18/09/2026.
 *
 * Demandé après ce constat de l'exploitation : « certaines factures je ne suis
 * pas du tout d'accord avec leur traitement », « plusieurs chiffres (BFR…) me
 * semblent démesurés et loin de la réalité ».
 *
 * CE FICHIER NE CALCULE AUCUN CHIFFRE NOUVEAU. Il répond à une seule question,
 * pièce par pièce : **cette facture entre-t-elle dans le BFR, et sinon
 * pourquoi ?** C'est la question qu'aucun écran ne savait poser, et sans elle
 * un total qui semble faux ne se remonte pas jusqu'à ses lignes.
 *
 * LA RÈGLE N'EST PAS RÉÉCRITE ICI, ELLE EST LUE. Le BFR décide par `resteA`
 * (bfr.ts) : une pièce compte quand son reste à la date d'arrêté est positif.
 * `compteAuBfr` ci-dessous applique les mêmes conditions, et un test les
 * confronte pièce à pièce — si l'une des deux change un jour, ce test tombe.
 * Deux formules qui divergent en silence seraient exactement le défaut qu'on
 * cherche à corriger.
 */

/** Le côté d'une pièce. Les deux copies Pennylane ont chacune leur séquence. */
export type SensFacture = 'client' | 'fournisseur';

/** Ce qu'on sait d'une exclusion posée à la main. */
export interface ExclusionFacture {
  readonly motif: string;
  readonly par: string;
  readonly le: string;
}

/**
 * Une facture telle que l'onglet la montre. Montants EN CENTIMES, comme
 * partout ailleurs dans Stafa.
 *
 * `ht` et `tva` SONT FACULTATIFS, et ce n'est pas un oubli : ils sont lus chez
 * Pennylane sous des noms relevés à travers un résumé de leur documentation
 * (`currency_amount_before_tax`, `tax`), et le HT n'est gardé que si la pièce
 * est en euros. L'écran affiche « — » plutôt qu'un zéro : un zéro se
 * confondrait avec un vrai montant nul.
 */
export interface FactureLue {
  readonly sens: SensFacture;
  readonly pennylane_id: string;
  readonly numero: string | null;
  readonly libelle: string | null;
  readonly tiers_nom: string;
  readonly date_piece: string | null;
  readonly echeance: string | null;
  readonly ht: number | null;
  readonly tva: number | null;
  readonly ttc: number;
  /** Reste dû chez Pennylane. Une pièce payée en rend 0. */
  readonly reste: number;
  readonly payee: boolean;
  /** Le statut tel que Pennylane le nomme, jamais interprété. */
  readonly statut: string | null;
  /** Date de paiement constatée, quand elle a été cherchée et trouvée. */
  readonly regle_le: string | null;
  /** La recherche de la date de paiement a-t-elle eu lieu ? */
  readonly regle_cherche: boolean;
  readonly cedee_le: string | null;
  /** Date de paiement PRÉVUE, calculée par l'échéancier. */
  readonly prevue_le: string | null;
  readonly ecartee: ExclusionFacture | null;
}

/** Pourquoi une pièce compte, ou ne compte pas. */
export type MotifCompte =
  'comptee' | 'ecartee' | 'sans_date' | 'date_future' | 'soldee' | 'reste_nul';

export const LIBELLES_MOTIF: Readonly<Record<MotifCompte, string>> = {
  comptee: 'comptée',
  ecartee: 'écartée à la main',
  sans_date: 'sans date de pièce',
  date_future: 'pièce datée dans le futur',
  soldee: 'soldée',
  reste_nul: 'reste dû nul ou négatif',
};

export interface SortFacture {
  readonly bfr: boolean;
  readonly motif: MotifCompte;
  /** Cédée au factor : comptée au BFR brut, puis retranchée du BFR net. */
  readonly cedee: boolean;
}

/**
 * CETTE PIÈCE ENTRE-T-ELLE DANS LE BFR D'AUJOURD'HUI, ET SINON POURQUOI ?
 *
 * L'ordre des cas est celui du diagnostic, pas celui du calcul : on nomme
 * d'abord ce qui relève d'une décision humaine (l'exclusion), puis ce qui
 * relève d'une donnée manquante, puis ce qui relève du cours normal des
 * choses. Une pièce à la fois écartée ET soldée doit s'afficher comme
 * écartée : c'est la décision qu'on veut pouvoir retrouver et lever.
 */
export function sortDeLaFacture(f: FactureLue, aujourdhui: string): SortFacture {
  const cedee = f.cedee_le !== null && f.cedee_le <= aujourdhui;
  const non = (motif: MotifCompte): SortFacture => ({ bfr: false, motif, cedee });

  if (f.ecartee !== null) return non('ecartee');
  // Sans date de pièce, le BFR ne lit même pas la ligne (`analyse_bfr.ts` :
  // `where date_piece is not null`). Elle est invisible partout ailleurs.
  if (f.date_piece === null) return non('sans_date');
  if (f.date_piece > aujourdhui) return non('date_future');
  // `resteA` rend 0 dès que la pièce est réglée à la date d'arrêté.
  if (f.payee || (f.regle_le !== null && f.regle_le <= aujourdhui))
    return non('soldee');
  if (f.reste <= 0) return non('reste_nul');
  return { bfr: true, motif: 'comptee', cedee };
}

/** Raccourci de lecture, pour les endroits qui n'ont pas besoin du motif. */
export function compteAuBfr(f: FactureLue, aujourdhui: string): boolean {
  return sortDeLaFacture(f, aujourdhui).bfr;
}

/**
 * LES DEUX TOTAUX, JAMAIS LEUR SOMME — arbitrage du 18/09/2026.
 *
 * L'onglet montre clients et fournisseurs dans un seul tableau, à la demande.
 * Additionner une créance et une dette donnerait pourtant un nombre qui ne
 * veut rien dire. Les deux totaux restent donc séparés, toujours affichés
 * tous les deux, et rien ne les additionne.
 */
export interface TotauxFactures {
  readonly creances: number;
  readonly dettes: number;
  readonly creances_comptees: number;
  readonly dettes_comptees: number;
  readonly lignes: number;
  readonly comptees: number;
  readonly ecartees: number;
  /** Lignes dont le HT n'a pas pu être lu : la colonne HT y est vide. */
  readonly sans_ht: number;
}

export function totauxDe(
  factures: readonly FactureLue[],
  aujourdhui: string,
): TotauxFactures {
  let creances = 0;
  let dettes = 0;
  let creancesComptees = 0;
  let dettesComptees = 0;
  let comptees = 0;
  let ecartees = 0;
  let sansHt = 0;
  for (const f of factures) {
    const s = sortDeLaFacture(f, aujourdhui);
    if (f.sens === 'client') {
      creances += f.reste;
      if (s.bfr) creancesComptees += f.reste;
    } else {
      dettes += f.reste;
      if (s.bfr) dettesComptees += f.reste;
    }
    if (s.bfr) comptees += 1;
    if (s.motif === 'ecartee') ecartees += 1;
    if (f.ht === null) sansHt += 1;
  }
  return {
    creances,
    dettes,
    creances_comptees: creancesComptees,
    dettes_comptees: dettesComptees,
    lignes: factures.length,
    comptees,
    ecartees,
    sans_ht: sansHt,
  };
}

// ---------------------------------------------------------------------------
// Où cette facture est-elle prise en compte, et comment — lot 107
// ---------------------------------------------------------------------------

/**
 * Demandé le 18/09/2026 : « voir partout où elle est prise en compte et
 * comment ». C'est la question qui manquait pour faire confiance à un total.
 *
 * CHAQUE LIGNE CI-DESSOUS EST RELEVÉE DANS LE CODE, pas supposée :
 *   - BFR : `bfrSemaine` (bfr.ts) retient `resteA(p, d, auj)` au poste
 *     « Factures clients non encaissées » ou « Fournisseurs non payés » ; une
 *     pièce cédée entre au brut puis se retranche du net.
 *   - Échéancier : `partsDe` (echeancier.ts) découpe le reste en parts datées.
 *   - Trésorerie : `fluxAVenir` (tresorerie.ts, boucle « Pièces clients
 *     ouvertes ») reprend CES MÊMES PARTS, nature « clients » ou
 *     « fournisseurs », « factor » quand la pièce est cédée.
 *   - Indicateurs : `analyse_indicateurs.ts` — `bfr_net`, `dso` et `dpo`
 *     viennent de `bfrSemaine`/`delaiSemaine` ; `montant_echu` et
 *     `paye_a_lecheance` lisent les factures clientes directement.
 *   - Résultat : il vient de la BALANCE DES COMPTES, qui donne des totaux par
 *     compte et ne connaît aucune pièce. Il est listé quand même, avec cette
 *     raison : un écran absent de la liste laisserait croire à un oubli.
 */
export type EcranPris =
  'bfr' | 'echeancier' | 'tresorerie' | 'indicateurs' | 'resultat';

export interface PriseEnCompte {
  readonly ecran: EcranPris;
  readonly nom: string;
  readonly compte: boolean;
  /** Comment, en une phrase — ce que l'écran en fait exactement. */
  readonly comment: string;
  /** Le montant retenu, quand il y en a un. */
  readonly montant: number | null;
  /** La date à laquelle il est retenu, quand il y en a une. */
  readonly quand: string | null;
}

/** Une part telle que `partsDe` la rend : de quoi dire quand et combien. */
export interface PartDatee {
  readonly date: string;
  readonly montant_centimes: number;
  readonly a_revoir: boolean;
}

export function prisesEnCompte(
  f: FactureLue,
  parts: readonly PartDatee[],
  aujourdhui: string,
): PriseEnCompte[] {
  const s = sortDeLaFacture(f, aujourdhui);
  const client = f.sens === 'client';
  const pourquoiPas =
    s.motif === 'ecartee'
      ? `écartée à la main${f.ecartee === null ? '' : ` : « ${f.ecartee.motif} »`}`
      : LIBELLES_MOTIF[s.motif];

  const bfr: PriseEnCompte = s.bfr
    ? {
        ecran: 'bfr',
        nom: 'BFR',
        compte: true,
        comment: s.cedee
          ? `comptée au poste « Factures cédées au factor » : elle entre dans le BFR brut, puis se retranche du BFR net`
          : `comptée au poste « ${client ? 'Factures clients non encaissées' : 'Fournisseurs non payés'} », pour son reste dû`,
        montant: f.reste,
        quand: aujourdhui,
      }
    : {
        ecran: 'bfr',
        nom: 'BFR',
        compte: false,
        comment: pourquoiPas,
        montant: null,
        quand: null,
      };

  const total = parts.reduce((s2, p) => s2 + p.montant_centimes, 0);
  const premiere = parts[0];
  const echeancier: PriseEnCompte =
    parts.length > 0 && s.bfr
      ? {
          ecran: 'echeancier',
          nom: 'Échéancier',
          compte: true,
          comment:
            parts.length === 1
              ? `un paiement prévu${premiere?.a_revoir === true ? ', date dépassée et ramenée à aujourd’hui' : ''}`
              : `${String(parts.length)} paiements prévus, du ${premiere?.date ?? '?'} au ${parts[parts.length - 1]?.date ?? '?'}`,
          montant: total,
          quand: premiere?.date ?? null,
        }
      : {
          ecran: 'echeancier',
          nom: 'Échéancier',
          compte: false,
          comment: s.bfr ? 'aucun paiement à prévoir' : pourquoiPas,
          montant: null,
          quand: null,
        };

  const tresorerie: PriseEnCompte =
    parts.length > 0 && s.bfr
      ? {
          ecran: 'tresorerie',
          nom: 'Trésorerie',
          compte: true,
          comment: s.cedee
            ? `la retenue du factor est rendue au paiement du client, nature « factor »`
            : `${client ? 'entrée' : 'sortie'} prévue, nature « ${client ? 'clients' : 'fournisseurs'} », aux dates de l’échéancier`,
          montant: total,
          quand: premiere?.date ?? null,
        }
      : {
          ecran: 'tresorerie',
          nom: 'Trésorerie',
          compte: false,
          comment: s.bfr ? 'aucun flux à venir' : pourquoiPas,
          montant: null,
          quand: null,
        };

  const lesquels = client
    ? 'BFR net, DSO, montant échu, payé à l’échéance'
    : 'BFR net, DPO';
  const indicateurs: PriseEnCompte = {
    ecran: 'indicateurs',
    nom: 'Indicateurs',
    compte: s.bfr,
    comment: s.bfr ? `entre dans : ${lesquels}` : pourquoiPas,
    montant: null,
    quand: null,
  };

  return [
    bfr,
    echeancier,
    tresorerie,
    indicateurs,
    {
      ecran: 'resultat',
      nom: 'Résultat',
      compte: false,
      comment:
        'jamais : le Résultat vient de la balance des comptes, qui donne des totaux par compte et ne connaît aucune facture. Écarter une pièce ne le change donc pas.',
      montant: null,
      quand: null,
    },
  ];
}
